import { useTranslation } from 'react-i18next';
import { Badge, type Tone } from '../../list/expense-labels';
import type { Supplier } from './api';

const STATUS_TONE: Record<Supplier['status'], Tone> = {
  active: 'success',
  unusable: 'neutral',
  merged: 'neutral',
};

const CHECK_TONE: Record<Supplier['identifierCheck'], Tone | null> = {
  verified: 'success',
  not_verifiable: 'neutral',
  invalid: 'error',
  unchecked: null,
};

/** The VAT number's check (VIES in phase 1); nothing while it is unchecked. */
export function IdentifierCheckBadge({ check }: { readonly check: Supplier['identifierCheck'] }) {
  const { t } = useTranslation('suppliers');
  const tone = CHECK_TONE[check];
  return tone === null ? null : <Badge tone={tone}>{t(`identifierCheck.${check}`)}</Badge>;
}

/**
 * The status, then the marks that coexist with it (§3.1, contract: status, indicators and marks
 * are kept apart): «Da completare», «Da verificare», «Intermediario», «Segnalato», and the VAT
 * check. Each is a word with its tone, never colour alone.
 */
export function SupplierMarks({ supplier }: { readonly supplier: Supplier }) {
  const { t } = useTranslation('suppliers');
  return (
    <ul aria-label={t('marks.label')} className="flex flex-wrap items-center gap-2">
      <li>
        <Badge tone={STATUS_TONE[supplier.status]}>{t(`status.${supplier.status}`)}</Badge>
      </li>
      {supplier.completeness === 'to_complete' ? (
        <li>
          <Badge tone="warning">{t('marks.toComplete')}</Badge>
        </li>
      ) : null}
      {supplier.toVerify ? (
        <li>
          <Badge tone="warning">{t('marks.toVerify')}</Badge>
        </li>
      ) : null}
      {supplier.intermediary ? (
        <li>
          <Badge tone="info">{t('marks.intermediary')}</Badge>
        </li>
      ) : null}
      {supplier.flagged ? (
        <li>
          <Badge tone="error">{t('marks.flagged')}</Badge>
        </li>
      ) : null}
      {supplier.identifierCheck !== 'unchecked' ? (
        <li>
          <IdentifierCheckBadge check={supplier.identifierCheck} />
        </li>
      ) : null}
    </ul>
  );
}
